import 'src/i18n/config';

import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dayjs from 'dayjs';

import { MotionLazyContainer } from './components/animate';
import { initializeGA4 } from './analytics';

// React Query 클라이언트 설정
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 데이터가 5분 동안 fresh로 간주됨
      staleTime: 5 * 60 * 1000,
      // 캐시는 30분 동안 유지
      gcTime: 30 * 60 * 1000,
      // 윈도우 포커스 시 자동 refetch 비활성화 (모바일 WebView 환경 고려)
      refetchOnWindowFocus: false,
      // 네트워크 재연결 시 refetch
      refetchOnReconnect: true,
      // 실패 시 3번까지 재시도
      retry: 3,
      // 재시도 간격 (지수 백오프)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // mutation은 재시도하지 않음
      retry: false,
    },
  },
});
import { setRootStoreSingleton } from './models/root-store/root-store-singleton';
import { RootStore, RootStoreProvider, setupRootStore } from './models';
import router from './routes';
import ThemeProvider from './theme';

function App() {
  const [rootStore, setRootStore] = useState<RootStore | undefined>(undefined);
  const { i18n } = useTranslation();

  useEffect(() => {
    initializeGA4(process.env.REACT_APP_GA4_MEASUREMENT_ID);
  }, []);

  useEffect(() => {
    // setup root store
    setupRootStore().then((store) => {
      setRootStore(store);
      setRootStoreSingleton(store);
      store.authStore.getUserProfile();
    });
  }, []);

  // 언어 변경 시 locale 변경
  useEffect(() => {
    dayjs.locale(i18n.language);
  }, [i18n.language]);

  if (!rootStore) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RootStoreProvider value={rootStore}>
        <HelmetProvider>
          <ThemeProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MotionLazyContainer>
                <RouterProvider router={router} />
              </MotionLazyContainer>
            </LocalizationProvider>
          </ThemeProvider>
        </HelmetProvider>
      </RootStoreProvider>
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
